/**
 * Firebase auth wiring: login form, logout button, auth state listener.
 *
 * `initAuth` takes callbacks for the signed-in and signed-out transitions
 * instead of importing loadData/stopListeners directly. That keeps this
 * module ignorant of what "signed in" means domain-wise and avoids a
 * circular dependency once state moves out of main.ts (Phase 5).
 *
 * Extracted from main.ts as part of OMON-031 Phase 3.
 */

import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  onAuthStateChanged,
  signOut,
  type User,
} from "firebase/auth";

import { auth } from "./firebase";
import {
  loginScreen,
  appScreen,
  loginForm,
  emailInput,
  passwordInput,
  loginBtn,
  loginError,
  logoutBtn,
  forgotPasswordBtn,
  resetStatus,
  emailLinkBtn,
  usePasswordBtn,
  emailLinkStatus,
} from "./dom";
import { mapAuthError } from "./account-password";
import {
  FINISH_LINK_PROMPT, LINK_FIRST_PROMPT, LINK_SENT,
  USE_LINK_INSTEAD, USE_PASSWORD_INSTEAD,
  errorCode, linkFailure, loginScreenMode, primaryActionLabel,
  restoresPasswordScreenOnSignOut,
} from "./email-link";
import { state } from "./state";
import { loadClaims } from "./admin/claims";
import { applyClaimsToShell } from "./admin/shell";
import {
  ANOTHER_POOL_MESSAGE, envRefusal, sessionFromAnotherPool,
} from "./env-gate";
import { appEnv } from "./firebase";
import { getIdTokenResult } from "firebase/auth";

// ADR-0038 3b part 2: the environment this bundle is bound to (project first,
// then database: a stc-nonproduction build is "nonprod", 2026-10-10).
const APP_ENV = appEnv;

export function initAuth(onSignedIn: () => void, onSignedOut: () => void) {
  // AFHR-030: tenant builds only — see the email-link section below.
  const emailLinkEnabled = typeof auth.tenantId === "string" && auth.tenantId !== "";

  // Set while the page is showing the finish-link screen: the full link URL,
  // which is half the credential (the typed email is the other half).
  let pendingLink: string | null = null;

  // Which door the card is currently showing. The submit button does a
  // DIFFERENT job in each mode, so this has to be explicit rather than
  // inferred from which elements happen to be hidden.
  let mode: "password" | "link-first" | "finish-link" =
    loginScreenMode(emailLinkEnabled, false);

  const showFinishLinkScreen = (link: string) => {
    mode = "finish-link";
    pendingLink = link;
    passwordInput.hidden = true;
    passwordInput.required = false;
    forgotPasswordBtn.hidden = true;
    resetStatus.hidden = true;
    emailLinkBtn.hidden = true;
    usePasswordBtn.hidden = true;
    loginBtn.textContent = primaryActionLabel("finish-link");
    emailLinkStatus.textContent = FINISH_LINK_PROMPT;
    emailInput.focus();
  };

  // ☠️ THE DEFAULT SCREEN ON A TENANT BUILD. The password box is hidden and
  // NOT required — leaving `required` on a hidden input makes the form
  // silently unsubmittable, with the browser trying to focus something it
  // cannot show. "Forgot your password?" is hidden too: on a tenant there is
  // no password to reset, so it is a dead end that reads like a lifeline.
  const showLinkFirstScreen = (message?: string) => {
    mode = "link-first";
    pendingLink = null;
    passwordInput.hidden = true;
    passwordInput.required = false;
    forgotPasswordBtn.hidden = true;
    resetStatus.hidden = true;
    emailLinkBtn.hidden = true;
    usePasswordBtn.hidden = false;
    loginBtn.textContent = primaryActionLabel("link-first");
    emailLinkStatus.textContent = message ?? LINK_FIRST_PROMPT;
  };

  // Back to the everyday screen, with the spent link stripped from the
  // address bar so a refresh can't land on it again.
  const showPasswordScreen = (message: string) => {
    mode = "password";
    pendingLink = null;
    window.history.replaceState({}, "", window.location.origin + "/");
    passwordInput.hidden = false;
    passwordInput.required = true;
    forgotPasswordBtn.hidden = false;
    resetStatus.hidden = false;
    // On a tenant, the way BACK to the link door; on production neither of
    // these ever unhides.
    emailLinkBtn.hidden = !emailLinkEnabled;
    usePasswordBtn.hidden = true;
    loginBtn.textContent = primaryActionLabel("password");
    emailLinkStatus.textContent = message;
  };

  // ONE implementation of "send me a link", called by the form's submit
  // button in link-first mode and by the secondary button from the password
  // screen. Previously this lived only on the button; the duplicate-email
  // bug of 2026-09-17 came from a second listener stacked on a shared
  // button, so there is deliberately one function and one caller per mode.
  const sendLink = async (): Promise<void> => {
    const email = emailInput.value.trim();
    if (!email) {
      emailLinkStatus.textContent = "Type your email in the box above first.";
      return;
    }
    emailLinkStatus.textContent = "Sending…";
    try {
      await sendSignInLinkToEmail(auth, email, {
        // The link returns to the app root; the interception below runs
        // before any routing, so no dedicated /finishSignIn route exists.
        url: `${window.location.origin}/`,
        handleCodeInApp: true,
      });
      emailLinkStatus.textContent = LINK_SENT;
    } catch (err) {
      emailLinkStatus.textContent = linkFailure(errorCode(err)).message;
    }
  };

  loginForm.addEventListener("submit", async (e: Event) => {
    e.preventDefault();
    loginBtn.disabled = true;
    loginError.textContent = "";
    if (pendingLink !== null) {
      try {
        await signInWithEmailLink(auth, emailInput.value.trim(), pendingLink);
        // Strip the oobCode from the address bar so a refresh (or a
        // shared screenshot) cannot replay a consumed link.
        window.history.replaceState({}, "", window.location.origin + "/");
        pendingLink = null;
      } catch (err) {
        const failure = linkFailure(errorCode(err));
        if (failure.keepLink) {
          emailLinkStatus.textContent = failure.message;
        } else {
          showPasswordScreen(failure.message);
        }
        loginBtn.disabled = false;
      }
      return;
    }
    // ☠️ On a tenant build the form's own submit button SENDS THE LINK. The
    // password path below would fail with auth/wrong-password for every real
    // invitee, because they have no password to be wrong about.
    if (mode === "link-first") {
      await sendLink();
      loginBtn.disabled = false;
      return;
    }
    try {
      await signInWithEmailAndPassword(auth, emailInput.value, passwordInput.value);
    } catch (err) {
      loginError.textContent = err instanceof Error ? err.message : "Login failed";
      loginBtn.disabled = false;
    }
  });

  logoutBtn.addEventListener("click", () => { signOut(auth); });

  // ── AFHR-030: the email-link door (tenant builds only) ──────────────
  //
  // Invitees are minted WITHOUT a password (ADR-0044 §4): possession of
  // the inbox is the credential. The door appears only when this bundle
  // is bound to an Identity Platform tenant — `auth.tenantId` is set in
  // firebase.ts from the build constant — so on .com/.dev/.xyz/.tech the
  // button stays hidden and the page is byte-identical in behaviour.
  //
  // No local storage of the email: re-typing it IS the same-device check
  // Firebase recommends, and it keeps an invitee's address out of the
  // browser store (the CoS door made the same choice).
  if (emailLinkEnabled) {
    // Labels come from the module, not the template, so the wording cannot
    // drift between the markup and the code that reasons about it.
    usePasswordBtn.textContent = USE_PASSWORD_INSTEAD;
    emailLinkBtn.textContent = USE_LINK_INSTEAD;

    // The email link is now the DEFAULT door here, not an afterthought.
    showLinkFirstScreen();

    emailLinkBtn.addEventListener("click", () => { showLinkFirstScreen(); });
    usePasswordBtn.addEventListener("click", () => { showPasswordScreen(""); });

    // Landing half: the URL IS the credential half, the typed email is the
    // other. Runs at init so a signed-out invitee arriving on the link sees a
    // screen with one job — confirm your email — rather than a password form.
    const href = window.location.href;
    if (loginScreenMode(emailLinkEnabled, isSignInWithEmailLink(auth, href)) === "finish-link") {
      showFinishLinkScreen(href);
    }
  }

  // Self-service password reset. Staff frequently let the emailed reset link
  // expire (it lasts ~1h; they're mid-shift at their desks) and had to ask an
  // admin to re-issue one by hand in the Firebase console. This lets them mint
  // a FRESH link on demand, the moment they're ready to act on it — automating
  // the manual console step. Enumeration-safe: the confirmation reads the same
  // whether or not the email maps to a real account.
  forgotPasswordBtn.addEventListener("click", async () => {
    const email = emailInput.value.trim();
    if (!email) {
      resetStatus.textContent = "Type your email in the box above first, then tap “Forgot your password?”.";
      return;
    }
    forgotPasswordBtn.disabled = true;
    resetStatus.textContent = "Sending…";
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      const code = (err as { code?: string }).code ?? "";
      if (code === "auth/invalid-email") {
        resetStatus.textContent = "That doesn't look like a valid email address.";
        forgotPasswordBtn.disabled = false;
        return;
      }
      // Anything other than "no such account" is a real failure the user can
      // act on (network, rate-limit). user-not-found falls through to the same
      // generic confirmation below so we don't reveal which emails are staff.
      if (code && code !== "auth/user-not-found") {
        resetStatus.textContent = mapAuthError(code);
        forgotPasswordBtn.disabled = false;
        return;
      }
    }
    resetStatus.textContent =
      `If an account exists for ${email}, a reset link is on its way. It expires in about an hour, so please use it as soon as you can.`;
    forgotPasswordBtn.disabled = false;
  });

  onAuthStateChanged(auth, async (user: User | null) => {
    if (user) {
      // ☠️ POOL gate, before the environment gate below — the two are
      // different axes and only the second used to exist. A session saved
      // before this surface moved into a tenant is restored from the MAIN
      // pool; its env claim can still match (both copies of demo.planner@
      // carry env: demo), so the environment gate admitted it on an orphaned
      // credential. See sessionFromAnotherPool in env-gate.ts.
      if (sessionFromAnotherPool(user.tenantId, auth.tenantId)) {
        await signOut(auth);
        loginError.textContent = ANOTHER_POOL_MESSAGE;
        return;
      }
      // ADR-0038 3b part 2 — environment gate, BEFORE anything renders or
      // reads. Fires for a fresh sign-in and for a restored session alike, so
      // a token from another environment (a demo persona on .com, a real
      // account on .xyz) is signed out to the login screen with a message
      // saying where it belongs — never a spinner, never this database's data.
      let tokenEnv: unknown = undefined;
      try {
        tokenEnv = (await getIdTokenResult(user, true)).claims["env"];
      } catch {
        try { tokenEnv = (await getIdTokenResult(user, false)).claims["env"]; } catch { /* unreadable: don't lock out */ }
      }
      const refusal = envRefusal(APP_ENV, tokenEnv);
      if (refusal !== null) {
        await signOut(auth);
        loginError.textContent = refusal;
        return;
      }
      loginScreen.style.display = "none";
      appScreen.style.display = "block";
      // OMON-137: load claims with forceRefresh=true so admin tooling
      // changes since sign-in are picked up. applyClaimsToShell decides
      // whether the hamburger renders. Awaits before onSignedIn so the
      // first render sees the right state.
      state.authClaims = await loadClaims(user);
      applyClaimsToShell();
      onSignedIn();
    } else {
      // Tear down the admin surface before the rest of the app — the
      // hamburger button needs to disappear synchronously with
      // appScreen hiding.
      state.authClaims = null;
      state.adminPanel = null;
      applyClaimsToShell();
      onSignedOut();
      loginScreen.style.display = "flex";
      appScreen.style.display = "none";
      loginBtn.disabled = false;
      passwordInput.value = "";
      // A link sign-in that the environment gate then refused lands here
      // with the link already consumed: leave the finish-link screen.
      //
      // ☠️ THIS TESTED `passwordInput.hidden` UNTIL 2026-09-21, and that was
      // only ever a proxy for "we are stuck on the finish-link screen" —
      // true because, back then, the finish screen was the ONLY thing that
      // hid the password box. Adding the link-first default made the same
      // condition true on EVERY page load: Firebase fires this observer with
      // user === null on init, the guard saw a hidden password box, decided
      // we were stranded, and restored the password screen milliseconds
      // after showLinkFirstScreen() had set it. The new default was live in
      // the bundle and invisible on the page.
      //
      // ☠️ AND THE LINK MUST BE SPENT. Replacing the whole old condition
      // dropped its `pendingLink === null` half, which was doing real work:
      // someone who has just OPENED a sign-in link is still signed out, so
      // this observer fires with the finish screen up and the link unspent.
      // Asking only about `mode` tore that screen down on arrival and
      // showPasswordScreen() stripped the oobCode from the URL on the way
      // out, so the link could not be retried either (found 2026-09-21).
      if (restoresPasswordScreenOnSignOut(mode, pendingLink !== null)) {
        showPasswordScreen("");
      }
    }
  });
}
